import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Shield, ShieldCheck, ShieldAlert, Smartphone, KeyRound, Trash2,
  Monitor, Loader2, Copy, CheckCircle2, AlertTriangle, LogIn, LogOut,
  DollarSign, Users, Wallet, RefreshCcw, Activity, Save, Bell,
} from "lucide-react";

// Icon lookup for audit log entries
const ACTION_ICONS = {
  login: LogIn,
  login_failed: AlertTriangle,
  login_failed_2fa: AlertTriangle,
  login_flood_alert_sent: ShieldAlert,
  logout: LogOut,
  register: Users,
  token_refresh: RefreshCcw,
  session_revoked: Trash2,
  trusted_device_added: CheckCircle2,
  trusted_device_revoked: Trash2,
  security_settings_updated: Bell,
  "2fa_enabled": ShieldCheck,
  "2fa_disabled": ShieldAlert,
  "2fa_recovery_used": KeyRound,
  "2fa_recovery_regenerated": KeyRound,
  real_money_enabled: DollarSign,
  real_money_disabled: DollarSign,
  real_money_reset: DollarSign,
  earnings_withdraw: Wallet,
  publish_bot: Activity,
  password_reset_requested: KeyRound,
  password_reset: KeyRound,
};
const ACTION_LABELS = {
  login: "Signed in",
  login_failed: "Failed sign-in attempt",
  login_failed_2fa: "Wrong 2FA code",
  login_flood_alert_sent: "Repeated failed sign-ins detected",
  logout: "Signed out",
  register: "Account created",
  token_refresh: "Session refreshed",
  session_revoked: "Session revoked",
  "2fa_enabled": "2FA enabled",
  "2fa_disabled": "2FA disabled",
  "2fa_recovery_used": "Recovery code used",
  "2fa_recovery_regenerated": "New recovery codes generated",
  real_money_enabled: "Real-money trading enabled",
  real_money_disabled: "Real-money trading disabled",
  real_money_reset: "Onboarding reset",
  earnings_withdraw: "Withdrew signal fees",
  publish_bot: "Published a bot",
  password_reset_requested: "Password reset requested",
  password_reset: "Password changed",
};

const ago = (iso) => {
  if (!iso) return "—";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.floor(s)}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function SecurityCenter() {
  const [me, setMe] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [audit, setAudit] = useState([]);
  const [setup, setSetup] = useState(null);
  const [qr, setQr] = useState(null);
  const [totpCode, setTotpCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [m, s, a] = await Promise.all([
        api.get("/auth/me"),
        api.get("/auth/sessions"),
        api.get("/auth/audit-log?limit=50"),
      ]);
      setMe(m.data);
      setSessions(s.data.sessions || []);
      setAudit(a.data.events || []);
    } catch (e) { toast.error("Failed to load security data"); }
  };
  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
      const dataUrl = await QRCode.toDataURL(data.otpauth_uri, { margin: 2, width: 240 });
      setQr(dataUrl);
    } catch (e) { toast.error(e?.response?.data?.detail || "Setup failed"); }
    finally { setBusy(false); }
  };

  const confirmSetup = async () => {
    if (totpCode.length < 6) return toast.error("Enter the 6-digit code");
    setBusy(true);
    try {
      await api.post("/auth/2fa/verify", { code: totpCode });
      const { data } = await api.post("/auth/2fa/recovery-codes");
      setRecoveryCodes(data.codes);
      toast.success("2FA enabled — save your recovery codes");
      setSetup(null);
      setQr(null);
      setTotpCode("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Verification failed"); }
    finally { setBusy(false); }
  };

  const regenCodes = async () => {
    if (!window.confirm("Replace your existing recovery codes? Old codes stop working immediately.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/recovery-codes");
      setRecoveryCodes(data.codes);
      toast.success("New recovery codes generated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const disable2fa = async () => {
    const code = window.prompt("Enter your current 6-digit 2FA code to disable:");
    if (!code) return;
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code });
      toast.success("2FA disabled");
      setRecoveryCodes(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Disable failed"); }
    finally { setBusy(false); }
  };

  const revokeSession = async (id) => {
    if (!window.confirm("Revoke this session? The device will be logged out.")) return;
    try {
      await api.delete(`/auth/sessions/${id}`);
      toast.success("Session revoked");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const copyToClipboard = (t) => {
    navigator.clipboard?.writeText(t).then(() => toast.success("Copied"));
  };

  const twoFaEnabled = !!me?.totp_enabled;

  return (
    <div className="space-y-6" data-testid="security-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Account</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold mt-1 flex items-center gap-3">
          <Shield className="w-7 h-7 md:w-8 md:h-8 opacity-70" />
          Security center
        </h1>
        <div className="text-sm text-muted-foreground mt-1">2FA, active sessions, and everything that touched your account.</div>
      </div>

      {/* 2FA panel */}
      <Card className="p-5 rounded-2xl" data-testid="tfa-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
              <Smartphone className="w-3.5 h-3.5" /> Two-factor authentication
            </div>
            <div className="text-lg font-display font-semibold mt-1 flex items-center gap-2">
              {twoFaEnabled ? (<><ShieldCheck className="w-5 h-5 text-[hsl(var(--gain))]" /> Enabled</>)
                            : (<><ShieldAlert className="w-5 h-5 text-[hsl(var(--ai))]" /> Not enabled</>)}
            </div>
            <div className="text-sm text-muted-foreground mt-1 max-w-xl">
              {twoFaEnabled
                ? "A 6-digit code from your authenticator is required at sign-in and to enable real-money trading."
                : "Add a Google Authenticator / 1Password TOTP to protect your account. Required for real-money trading."}
            </div>
          </div>
          <div className="flex gap-2">
            {!twoFaEnabled && !setup && (
              <Button onClick={startSetup} disabled={busy} data-testid="tfa-setup-btn" className="rounded-full">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                Enable 2FA
              </Button>
            )}
            {twoFaEnabled && (
              <>
                <Button variant="outline" onClick={regenCodes} disabled={busy}
                        data-testid="tfa-regen-btn" className="rounded-full">
                  <KeyRound className="w-4 h-4 mr-2" /> New recovery codes
                </Button>
                <Button variant="outline" onClick={disable2fa} disabled={busy}
                        data-testid="tfa-disable-btn" className="rounded-full text-[hsl(var(--loss))]">
                  Disable
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Setup wizard */}
        {setup && (
          <div className="mt-5 p-5 rounded-xl border border-border/60 bg-secondary/20" data-testid="tfa-setup-panel">
            <div className="grid md:grid-cols-[220px_1fr] gap-6 items-start">
              <div className="mx-auto md:mx-0 bg-white p-2 rounded-lg" data-testid="tfa-qr">
                {qr && <img src={qr} alt="scan" width={200} height={200} />}
              </div>
              <div className="space-y-3">
                <div>
                  <div className="text-sm font-medium">1. Scan the QR code</div>
                  <div className="text-xs text-muted-foreground">In Google Authenticator, 1Password, Authy, or any TOTP app. Can't scan? Enter this key manually:</div>
                  <div className="mt-1 flex items-center gap-2">
                    <code data-testid="tfa-secret" className="font-mono-tab text-xs bg-secondary rounded px-2 py-1 select-all">{setup.secret}</code>
                    <button onClick={() => copyToClipboard(setup.secret)}
                            className="text-muted-foreground hover:text-foreground" title="Copy">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">2. Enter the 6-digit code</Label>
                  <div className="flex gap-2 mt-1">
                    <Input inputMode="numeric" pattern="\d{6}" maxLength={8} placeholder="123456"
                           value={totpCode}
                           onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                           data-testid="tfa-verify-input"
                           className="font-mono-tab text-lg w-32" />
                    <Button onClick={confirmSetup} disabled={busy || totpCode.length < 6}
                            data-testid="tfa-verify-btn" className="rounded-full">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & enable"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recovery codes reveal */}
        {recoveryCodes && (
          <div className="mt-5 p-5 rounded-xl border border-[hsl(var(--ai))]/40 bg-[hsl(var(--ai))]/5 space-y-3" data-testid="tfa-recovery-panel">
            <div className="flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-[hsl(var(--ai))]" />
              <div className="font-semibold">Save your recovery codes</div>
            </div>
            <div className="text-xs text-muted-foreground">
              Each code works ONCE if you lose your authenticator. They're shown here only ONCE — save them somewhere safe (password manager works great).
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 font-mono-tab text-sm">
              {recoveryCodes.map((c, i) => (
                <div key={i} data-testid={`tfa-recovery-code-${i}`} className="p-2 rounded bg-background border border-border/60 text-center select-all">
                  {c}
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => copyToClipboard(recoveryCodes.join("\n"))}
                      data-testid="tfa-recovery-copy" className="rounded-full">
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy all
              </Button>
              <Button size="sm" onClick={() => setRecoveryCodes(null)}
                      data-testid="tfa-recovery-done" className="rounded-full">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> I saved them
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Active sessions */}
      <Card className="p-5 rounded-2xl" data-testid="sessions-card">
        <div className="flex items-center gap-2 mb-4">
          <Monitor className="w-4 h-4 opacity-70" />
          <h2 className="font-display font-semibold text-lg">Active sessions</h2>
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground bg-secondary/60 rounded-full px-2 py-0.5">
            {sessions.length}
          </span>
        </div>
        {sessions.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center">No active sessions</div>
        ) : (
          <div className="space-y-2">
            {sessions.map((s) => (
              <div key={s.id} data-testid={`session-${s.id}`}
                   className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border/60">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{s.user_agent || "Unknown device"}</div>
                  <div className="text-[11px] text-muted-foreground font-mono-tab mt-0.5">
                    IP: {s.ip || "—"} · created {ago(s.created_at)}
                    {s.last_used_at && <> · used {ago(s.last_used_at)}</>}
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => revokeSession(s.id)}
                        data-testid={`session-revoke-${s.id}`}
                        className="rounded-full text-[hsl(var(--loss))]">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Revoke
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Audit log */}
      <Card className="p-5 rounded-2xl" data-testid="audit-card">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-4 h-4 opacity-70" />
          <h2 className="font-display font-semibold text-lg">Recent activity</h2>
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground bg-secondary/60 rounded-full px-2 py-0.5">
            {audit.length}
          </span>
        </div>
        {audit.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center">Nothing yet</div>
        ) : (
          <div className="space-y-1">
            {audit.map((e, i) => {
              const Icon = ACTION_ICONS[e.action] || Activity;
              const isFail = e.action.includes("failed") || e.action.includes("flood");
              return (
                <div key={i} data-testid={`audit-row-${i}`}
                     className="flex items-center gap-3 py-2 px-2 rounded hover:bg-secondary/40">
                  <Icon className={`w-4 h-4 flex-shrink-0 ${isFail ? "text-[hsl(var(--loss))]" : "text-muted-foreground"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{ACTION_LABELS[e.action] || e.action}</div>
                    <div className="text-[10px] text-muted-foreground font-mono-tab">
                      {ago(e.t)} · IP {e.ip || "—"}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
